import { useEffect, useState } from 'react'

/** Height of the fixed header; the probe line sits just below it. */
export const HEADER_HEIGHT = 80

/** A section reduced to the numbers the choice depends on. */
export interface SectionPosition {
  id: string
  /** Viewport-relative top edge, i.e. `getBoundingClientRect().top`. */
  top: number
  /** Document-relative top edge, used only to establish page order. */
  offsetTop: number
}

/**
 * Choose the section under the header.
 *
 * The rule is deliberately literal: of the sections whose top edge has already
 * passed the probe line just below the header, the last one in page order wins.
 * That is the section the reader is actually looking at.
 *
 * At the very bottom of the page the final section may be too short to ever
 * reach the probe line — nothing below it can win, so `atBottom` awards it
 * directly. Without that, a short closing section can never light up.
 *
 * Pure and separated from the DOM so the edge cases are unit-testable; an
 * earlier IntersectionObserver version was not, and quietly kept the underline
 * on the outgoing section when a boundary landed exactly on the rootMargin.
 */
export function pickActiveSection(
  sections: readonly SectionPosition[],
  {
    atBottom = false,
    headerHeight = HEADER_HEIGHT,
  }: { atBottom?: boolean; headerHeight?: number } = {},
): string | null {
  if (sections.length === 0) return null

  const ordered = [...sections].sort((a, b) => a.offsetTop - b.offsetTop)
  if (atBottom) return ordered[ordered.length - 1].id

  let current = ordered[0].id
  for (const section of ordered) {
    // +1 absorbs sub-pixel rounding when a section lands flush under the header.
    if (section.top <= headerHeight + 1) current = section.id
  }
  return current
}

/**
 * Reports which section is currently under the header, for the nav underline.
 *
 * `sectionIds` must be referentially stable — pass a module-level constant.
 */
export function useActiveSection(sectionIds: readonly string[]): string | null {
  const [activeId, setActiveId] = useState<string | null>(sectionIds[0] ?? null)

  useEffect(() => {
    let frame = 0

    const compute = () => {
      frame = 0

      const sections = sectionIds
        .map((id) => document.getElementById(id))
        .filter((el): el is HTMLElement => el !== null)
        .map((el) => ({ id: el.id, top: el.getBoundingClientRect().top, offsetTop: el.offsetTop }))

      // clientHeight rather than window.innerHeight: it stays correct when the
      // page is in a background/hidden document, where innerHeight can read 0.
      const viewportHeight = document.documentElement.clientHeight
      const atBottom = viewportHeight + window.scrollY >= document.documentElement.scrollHeight - 2

      const next = pickActiveSection(sections, { atBottom })
      if (next) setActiveId(next)
    }

    const onScroll = () => {
      if (frame) return
      frame = requestAnimationFrame(compute)
    }

    compute()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)

    return () => {
      if (frame) cancelAnimationFrame(frame)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [sectionIds])

  return activeId
}
